import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, ChevronDown, Trophy, Swords, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MatchupRow {
  id: string;
  week: number;
  // SAB-188: TBD until the previous round resolves. Every read site below is
  // optional-chained with a 'TBD' fallback already -- this makes the type say so.
  home_team_id: string | null;
  away_team_id: string | null;
  home_score: number;
  away_score: number;
  status: string;
  is_playoff: boolean;
  is_championship: boolean;
  playoff_round: string | null;
}

// 2025 NFL fantasy season week dates (Thu–Mon)
const NFL_WEEK_DATES: Record<number, [string, string]> = {
  1: ['Sep 4', 'Sep 8'],
  2: ['Sep 11', 'Sep 15'],
  3: ['Sep 18', 'Sep 22'],
  4: ['Sep 25', 'Sep 29'],
  5: ['Oct 2', 'Oct 6'],
  6: ['Oct 9', 'Oct 13'],
  7: ['Oct 16', 'Oct 20'],
  8: ['Oct 23', 'Oct 27'],
  9: ['Oct 30', 'Nov 3'],
  10: ['Nov 6', 'Nov 10'],
  11: ['Nov 13', 'Nov 17'],
  12: ['Nov 20', 'Nov 24'],
  13: ['Nov 27', 'Dec 1'],
  14: ['Dec 4', 'Dec 8'],
  15: ['Dec 11', 'Dec 15'],
  16: ['Dec 18', 'Dec 22'],
};

export default function NFLSchedulePage() {
  const { currentLeague, currentTeam, currentWeek, teams, isLoading: contextLoading } = useNFLLeague();
  const [selectedWeek, setSelectedWeek] = useState(currentWeek);
  const [tab, setTab] = useState<'schedule' | 'playoffs'>(currentWeek > 14 ? 'playoffs' : 'schedule');

  const teamMap = useMemo(() => {
    const map: Record<string, { id: string; name: string; wins: number; losses: number }> = {};
    for (const t of teams) map[t.id] = { id: t.id, name: t.name, wins: (t as any).wins ?? 0, losses: (t as any).losses ?? 0 };
    return map;
  }, [teams]);

  // Seeds for the bracket: rank teams by wins, then points_for as tiebreak.
  // The schedule's bracket needs these to display "#1 Foo vs #4 Bar".
  const seedMap = useMemo(() => {
    const seeded = [...teams].sort((a, b) => {
      const wa = (a as any).wins ?? 0;
      const wb = (b as any).wins ?? 0;
      if (wb !== wa) return wb - wa;
      const pfa = (a as any).points_for ?? 0;
      const pfb = (b as any).points_for ?? 0;
      return pfb - pfa;
    });
    const map: Record<string, number> = {};
    seeded.forEach((t, i) => { map[t.id] = i + 1; });
    return map;
  }, [teams]);

  // Full-season schedule for the league. Own key (season-wide, not the week-scoped shared
  // matchups key). isPending gates the spinner, so returning to this tab shows the cached
  // schedule instantly and refetches in the background — no blank (SAB-830). staleTime 60s:
  // the schedule is near-static within a session; scores update but not urgently here.
  const { data: allMatchups = [], isPending } = useQuery({
    queryKey: ['nfl-schedule', currentLeague?.id],
    enabled: !!currentLeague,
    staleTime: 60_000,
    queryFn: async (): Promise<MatchupRow[]> => {
      const { data, error } = await supabase
        .from('nfl_matchups' as any)
        .select('id, week, home_team_id, away_team_id, home_score, away_score, status, is_playoff, is_championship, playoff_round')
        .eq('league_id', currentLeague!.id)
        .order('week')
        .order('id');
      if (error) { console.error('Failed to fetch schedule:', error); throw error; }
      return (data || []) as MatchupRow[];
    },
  });

  // Regular season: weeks 1-14, Playoffs: 15-16
  const maxRegularWeek = 14;
  const weekMatchups = useMemo(() => allMatchups.filter(m => m.week === selectedWeek), [allMatchups, selectedWeek]);
  const playoffMatchups = useMemo(() => allMatchups.filter(m => m.week > maxRegularWeek), [allMatchups]);

  // My matchup this week
  const myMatchup = useMemo(() => {
    if (!currentTeam) return null;
    return weekMatchups.find(m => m.home_team_id === currentTeam.id || m.away_team_id === currentTeam.id) || null;
  }, [weekMatchups, currentTeam]);

  const otherMatchups = useMemo(() => {
    if (!myMatchup) return weekMatchups;
    return weekMatchups.filter(m => m.id !== myMatchup.id);
  }, [weekMatchups, myMatchup]);

  const goWeek = (dir: -1 | 1) => {
    setSelectedWeek(w => Math.max(1, Math.min(maxRegularWeek, w + dir)));
  };

  if (contextLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!currentLeague) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Calendar className="h-10 w-10 text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground">Join a league to see the schedule</p>
      </div>
    );
  }

  // Only the genuine first load (no cached data) blanks to a spinner; a background refetch
  // on tab return keeps the cached schedule on screen.
  if (isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20 md:pb-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-display font-bold text-foreground flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary" />
          Fantasy Schedule
        </h1>
      </div>

      {/* Schedule / Playoffs toggle */}
      <div className="flex items-center gap-1 p-1 bg-muted/30 rounded-lg w-fit">
        {(['schedule', 'playoffs'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'px-4 py-1.5 rounded-md text-xs font-semibold transition-all',
              tab === t
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {t === 'schedule' ? 'Schedule' : 'Playoffs'}
          </button>
        ))}
      </div>

      {tab === 'schedule' ? (
        <>
          {/* Horizontal week strip — scrollable on mobile, current week
              highlighted in orange. Replaces the previous Prev/Next-only
              header which left the whole regular-season schedule invisible
              until the user clicked through. */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => goWeek(-1)}
              disabled={selectedWeek <= 1}
              className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors shrink-0"
              aria-label="Previous week"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex-1 overflow-x-auto no-scrollbar">
              <div className="flex gap-1.5">
                {Array.from({ length: maxRegularWeek }, (_, i) => i + 1).map(w => (
                  <button
                    key={w}
                    onClick={() => setSelectedWeek(w)}
                    className={cn(
                      'shrink-0 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all',
                      selectedWeek === w
                        ? 'bg-primary text-primary-foreground shadow-md'
                        : w === currentWeek
                          ? 'bg-primary/10 text-primary border border-primary/30'
                          : 'bg-muted/30 text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    )}
                  >
                    Wk {w}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={() => goWeek(1)}
              disabled={selectedWeek >= maxRegularWeek}
              className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors shrink-0"
              aria-label="Next week"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Week label + dates */}
          <div className="text-center">
            <p className="text-sm font-bold text-foreground">Week {selectedWeek}</p>
            {NFL_WEEK_DATES[selectedWeek] && (
              <p className="text-[11px] text-muted-foreground">
                {NFL_WEEK_DATES[selectedWeek][0]} – {NFL_WEEK_DATES[selectedWeek][1]}
              </p>
            )}
          </div>

          {/* YOUR MATCHUP */}
          {myMatchup && (
            <div>
              <p className="text-[10px] text-primary font-bold uppercase tracking-wider mb-1.5">Your Matchup</p>
              <MatchupCard matchup={myMatchup} teamMap={teamMap} myTeamId={currentTeam?.id} highlight />
            </div>
          )}

          {/* ALL MATCHUPS */}
          {otherMatchups.length > 0 && (
            <div>
              <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider mb-1.5">
                {myMatchup ? 'Other Matchups' : 'All Matchups'}
              </p>
              <div className="space-y-2">
                {otherMatchups.map(m => (
                  <MatchupCard key={m.id} matchup={m} teamMap={teamMap} myTeamId={currentTeam?.id} />
                ))}
              </div>
            </div>
          )}

          {weekMatchups.length === 0 && (
            <Card className="bg-card border-border">
              <CardContent className="py-10 text-center">
                <p className="text-sm text-muted-foreground">No matchups this week</p>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        /* Playoffs Bracket */
        <PlayoffBracket matchups={playoffMatchups} teamMap={teamMap} seedMap={seedMap} myTeamId={currentTeam?.id} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Matchup Card                                                       */
/* ------------------------------------------------------------------ */
function MatchupCard({
  matchup, teamMap, myTeamId, highlight,
}: {
  matchup: MatchupRow;
  teamMap: Record<string, { id: string; name: string }>;
  myTeamId?: string;
  highlight?: boolean;
}) {
  const home = teamMap[matchup.home_team_id];
  const away = teamMap[matchup.away_team_id];
  const hScore = Number(matchup.home_score) || 0;
  const aScore = Number(matchup.away_score) || 0;
  const isFinal = matchup.status === 'final' || matchup.status === 'completed';
  const homeWins = isFinal && hScore > aScore;
  const awayWins = isFinal && aScore > hScore;

  return (
    <Card className={cn(
      'bg-card border-border overflow-hidden',
      highlight && 'border-primary/40 shadow-[0_0_12px_rgba(234,88,12,0.08)]'
    )}>
      <CardContent className="p-0">
        <div className="flex items-center px-4 py-3">
          {/* Away team */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              {awayWins && (
                <span title="Matchup winner" className="shrink-0 inline-flex">
                  <Trophy className="h-3 w-3 text-amber-400" aria-label="Matchup winner" />
                </span>
              )}
              <span className={cn(
                'text-sm font-semibold truncate',
                matchup.away_team_id === myTeamId ? 'text-primary' : 'text-foreground'
              )} title={away?.name || 'TBD'}>
                {away?.name || 'TBD'}
              </span>
            </div>
          </div>

          {/* Scores + badge */}
          <div className="flex items-center gap-2 shrink-0 mx-3">
            <span className={cn(
              'text-base font-bold tabular-nums',
              awayWins ? 'text-foreground' : isFinal ? 'text-muted-foreground' : 'text-foreground'
            )}>
              {isFinal ? aScore.toFixed(1) : '--'}
            </span>
            <div className="flex flex-col items-center">
              {isFinal ? (
                <Badge variant="outline" className="text-[8px] px-1.5 py-0 border-border text-muted-foreground">
                  FINAL
                </Badge>
              ) : (
                <span className="text-[9px] text-muted-foreground">vs</span>
              )}
            </div>
            <span className={cn(
              'text-base font-bold tabular-nums',
              homeWins ? 'text-foreground' : isFinal ? 'text-muted-foreground' : 'text-foreground'
            )}>
              {isFinal ? hScore.toFixed(1) : '--'}
            </span>
          </div>

          {/* Home team */}
          <div className="flex-1 min-w-0 text-right">
            <div className="flex items-center gap-1.5 justify-end">
              <span className={cn(
                'text-sm font-semibold truncate',
                matchup.home_team_id === myTeamId ? 'text-primary' : 'text-foreground'
              )} title={home?.name || 'TBD'}>
                {home?.name || 'TBD'}
              </span>
              {homeWins && (
                <span title="Matchup winner" className="shrink-0 inline-flex">
                  <Trophy className="h-3 w-3 text-amber-400" aria-label="Matchup winner" />
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  Playoff Bracket                                                    */
/* ------------------------------------------------------------------ */
function PlayoffBracket({
  matchups, teamMap, seedMap, myTeamId,
}: {
  matchups: MatchupRow[];
  teamMap: Record<string, { id: string; name: string; wins?: number; losses?: number }>;
  seedMap: Record<string, number>;
  myTeamId?: string;
}) {
  // Sort playoff matchups by week then by championship flag
  const sorted = [...matchups].sort((a, b) => {
    if (a.week !== b.week) return a.week - b.week;
    if (a.is_championship && !b.is_championship) return 1;
    return 0;
  });

  // Separate into rounds using playoff_round column (with fallback to old logic)
  const wildCard = sorted.filter(m => m.playoff_round === 'wild_card');
  const semis = sorted.filter(m =>
    m.playoff_round === 'semifinal' ||
    (!m.playoff_round && !m.is_championship && m.week === Math.min(...sorted.map(s => s.week)))
  );
  const finals = sorted.filter(m => m.playoff_round === 'championship' || (!m.playoff_round && m.is_championship));
  const consolation = sorted.filter(m => m.playoff_round === 'consolation');

  if (sorted.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="py-12 text-center">
          <Trophy className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground font-medium">Playoffs haven't started yet</p>
          <p className="text-xs text-muted-foreground mt-1">Top 4 teams qualify after Week 14</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Championship Banner */}
      {finals.length > 0 && (() => {
        const champ = finals[0];
        const hScore = Number(champ.home_score) || 0;
        const aScore = Number(champ.away_score) || 0;
        const isFinal = champ.status === 'final' || champ.status === 'completed';
        const winnerId = isFinal ? (hScore > aScore ? champ.home_team_id : champ.away_team_id) : null;
        const winnerName = winnerId ? teamMap[winnerId]?.name : null;

        return (
          <Card className="bg-gradient-to-b from-amber-500/10 to-card border-amber-500/30 overflow-hidden">
            <CardContent className="p-6 text-center">
              <Trophy className="h-10 w-10 text-amber-400 mx-auto mb-2" />
              <p className="text-[10px] text-amber-400/80 font-bold uppercase tracking-wider mb-1">Championship</p>
              {isFinal && winnerName ? (
                <>
                  <p className="text-xl font-display font-bold text-foreground mb-1">{winnerName}</p>
                  <p className="text-xs text-muted-foreground">League Champion 2025</p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Championship in progress</p>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Wild card — pre-semis round, kept above the bracket viz */}
      {wildCard.length > 0 && (
        <div>
          <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider mb-2">
            Wild Card: Week {wildCard[0].week}
          </p>
          <div className="space-y-2">
            {wildCard.map(m => (
              <BracketMatchup key={m.id} matchup={m} teamMap={teamMap} seedMap={seedMap} myTeamId={myTeamId} />
            ))}
          </div>
        </div>
      )}

      {/* Bracket viz — Semifinals → Championship.
          Desktop: horizontal layout with connecting lines.
          Mobile (<md): stacked vertically with chevron indicators between rounds. */}
      {(semis.length > 0 || finals.length > 0) && (
        <div>
          <div className="hidden md:flex items-stretch gap-6">
            {/* Semifinals column */}
            {semis.length > 0 && (
              <div className="flex-1">
                <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider mb-2">
                  Semifinals: Week {semis[0].week}
                </p>
                <div className="flex flex-col justify-around gap-6 h-full">
                  {semis.map(m => (
                    <BracketMatchup key={m.id} matchup={m} teamMap={teamMap} seedMap={seedMap} myTeamId={myTeamId} />
                  ))}
                </div>
              </div>
            )}

            {/* Connecting lines */}
            {semis.length > 0 && finals.length > 0 && (
              <div className="flex flex-col items-center justify-center w-8 shrink-0" aria-hidden="true">
                <div className="w-full border-t-2 border-amber-500/40" />
                <div className="h-24 border-r-2 border-amber-500/40" />
                <div className="w-full border-t-2 border-amber-500/40" />
              </div>
            )}

            {/* Championship column */}
            {finals.length > 0 && (
              <div className="flex-1">
                <p className="text-[10px] text-amber-400 font-bold uppercase tracking-wider mb-2">
                  Championship: Week {finals[0].week}
                </p>
                <div className="flex flex-col justify-center gap-2 h-full">
                  {finals.map(m => (
                    <BracketMatchup key={m.id} matchup={m} teamMap={teamMap} seedMap={seedMap} myTeamId={myTeamId} isChampionship />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Mobile stacked layout */}
          <div className="md:hidden space-y-3">
            {semis.length > 0 && (
              <div>
                <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider mb-2">
                  Semifinals: Week {semis[0].week}
                </p>
                <div className="space-y-2">
                  {semis.map(m => (
                    <BracketMatchup key={m.id} matchup={m} teamMap={teamMap} seedMap={seedMap} myTeamId={myTeamId} />
                  ))}
                </div>
              </div>
            )}

            {semis.length > 0 && finals.length > 0 && (
              <div className="flex justify-center" aria-hidden="true">
                <ChevronDown className="h-5 w-5 text-amber-500/60" />
              </div>
            )}

            {finals.length > 0 && (
              <div>
                <p className="text-[10px] text-amber-400 font-bold uppercase tracking-wider mb-2">
                  Championship: Week {finals[0].week}
                </p>
                <div className="space-y-2">
                  {finals.map(m => (
                    <BracketMatchup key={m.id} matchup={m} teamMap={teamMap} seedMap={seedMap} myTeamId={myTeamId} isChampionship />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {consolation.length > 0 && (
        <div>
          <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider mb-2">
            Consolation: Week {consolation[0].week}
          </p>
          <div className="space-y-2">
            {consolation.map(m => (
              <BracketMatchup key={m.id} matchup={m} teamMap={teamMap} seedMap={seedMap} myTeamId={myTeamId} />
            ))}
          </div>
        </div>
      )}

      {/* Legend — explains the trophy icon used on winning teams. */}
      <div className="flex items-center gap-4 text-[11px] text-muted-foreground pt-2 border-t border-border/40">
        <div className="flex items-center gap-1.5">
          <Trophy className="h-3 w-3 text-amber-400" />
          <span>Round winner</span>
        </div>
      </div>
    </div>
  );
}

function BracketMatchup({
  matchup, teamMap, seedMap, myTeamId, isChampionship,
}: {
  matchup: MatchupRow;
  teamMap: Record<string, { id: string; name: string }>;
  seedMap?: Record<string, number>;
  myTeamId?: string;
  isChampionship?: boolean;
}) {
  const home = teamMap[matchup.home_team_id];
  const away = teamMap[matchup.away_team_id];
  const hScore = Number(matchup.home_score) || 0;
  const aScore = Number(matchup.away_score) || 0;
  const isFinal = matchup.status === 'final' || matchup.status === 'completed';
  const homeWins = isFinal && hScore > aScore;
  const awayWins = isFinal && aScore > hScore;
  const awaySeed = seedMap?.[matchup.away_team_id];
  const homeSeed = seedMap?.[matchup.home_team_id];

  return (
    <Card className={cn(
      'bg-card border-border overflow-hidden',
      isChampionship && 'border-amber-500/30'
    )}>
      <CardContent className="p-0">
        {/* Away/Top seed */}
        <div className={cn(
          'flex items-center justify-between px-4 py-2.5 border-b border-border/50',
          awayWins && 'bg-primary/[0.03]'
        )}>
          <div className="flex items-center gap-2 min-w-0">
            {awaySeed && (
              <span className="text-[10px] font-bold text-muted-foreground tabular-nums shrink-0 w-5 text-center">
                #{awaySeed}
              </span>
            )}
            {awayWins && (
              <span title="Round winner" className="shrink-0 inline-flex">
                <Trophy className="h-3 w-3 text-amber-400" aria-label="Round winner" />
              </span>
            )}
            <span className={cn(
              'text-sm truncate',
              isFinal
                ? awayWins
                  ? cn('font-bold', matchup.away_team_id === myTeamId ? 'text-primary' : 'text-foreground')
                  : 'font-semibold text-muted-foreground'
                : cn('font-semibold', matchup.away_team_id === myTeamId ? 'text-primary' : 'text-foreground')
            )} title={away?.name || 'TBD'}>
              {away?.name || 'TBD'}
            </span>
          </div>
          <span className={cn(
            'text-sm font-bold tabular-nums',
            awayWins ? 'text-foreground' : isFinal ? 'text-muted-foreground' : 'text-foreground'
          )}>
            {isFinal ? aScore.toFixed(1) : '--'}
          </span>
        </div>
        {/* Home/Bottom seed */}
        <div className={cn(
          'flex items-center justify-between px-4 py-2.5',
          homeWins && 'bg-primary/[0.03]'
        )}>
          <div className="flex items-center gap-2 min-w-0">
            {homeSeed && (
              <span className="text-[10px] font-bold text-muted-foreground tabular-nums shrink-0 w-5 text-center">
                #{homeSeed}
              </span>
            )}
            {homeWins && (
              <span title="Round winner" className="shrink-0 inline-flex">
                <Trophy className="h-3 w-3 text-amber-400" aria-label="Round winner" />
              </span>
            )}
            <span className={cn(
              'text-sm truncate',
              isFinal
                ? homeWins
                  ? cn('font-bold', matchup.home_team_id === myTeamId ? 'text-primary' : 'text-foreground')
                  : 'font-semibold text-muted-foreground'
                : cn('font-semibold', matchup.home_team_id === myTeamId ? 'text-primary' : 'text-foreground')
            )} title={home?.name || 'TBD'}>
              {home?.name || 'TBD'}
            </span>
          </div>
          <span className={cn(
            'text-sm font-bold tabular-nums',
            homeWins ? 'text-foreground' : isFinal ? 'text-muted-foreground' : 'text-foreground'
          )}>
            {isFinal ? hScore.toFixed(1) : '--'}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
